import { Message } from "./messages";

const LOG_PREFIX = "[MessageProxy]";

type Listener = (payload?: unknown) => void;

export class MessageProxy {
  private listeners = new Map<string, Listener>();
  private readonly config: MessageProxyConfig;
  private readonly handleMessage: (event: MessageEvent) => void;

  constructor(config: MessageProxyConfig) {
    this.config = config;

    this.handleMessage = (event: MessageEvent<unknown>) => {
      if (!this.isValidOrigin(event.origin)) {
        if (this.config.debug) {
          console.warn(
            `${LOG_PREFIX} Rejected message from origin: ${event.origin}`
          );
        }
        return;
      }

      const { data } = event;
      if (
        !data ||
        typeof data !== "object" ||
        !("type" in data) ||
        typeof data.type !== "string"
      ) {
        return;
      }

      if (this.config.debug) {
        console.log(`${LOG_PREFIX} Received "${data.type}"`, data);
      }

      const handler = this.listeners.get(data.type);
      if (!handler) return;

      if ("payload" in data) {
        handler(data.payload);
      } else if ("action" in data) {
        handler(data.action);
      } else {
        handler();
      }
    };

    this.attach();
  }

  /**
   * Start listening for messages. Safe to call multiple times —
   * removes any previous listener first.
   */
  attach(): void {
    if (typeof window !== "undefined") {
      window.removeEventListener("message", this.handleMessage);
      window.addEventListener("message", this.handleMessage);
    }
  }

  send<T extends Message["type"]>(
    type: T,
    ...args: ExtractPayload<Message, T> extends never
      ? []
      : [payload: ExtractPayload<Message, T>]
  ): void {
    const targetWindow = this.config.getTargetWindow();
    if (targetWindow === window) {
      if (this.config.debug) {
        console.warn(`${LOG_PREFIX} Skipped "${type}" — target is self`);
      }
      return;
    }

    const [payload] = args;

    if (this.config.debug) {
      console.log(`${LOG_PREFIX} Sending "${type}"`, payload);
    }

    const message = payload !== undefined ? { type, payload } : { type };
    targetWindow?.postMessage(message, "*");
  }

  on<T extends Message["type"]>(
    type: T,
    ...args: ExtractPayload<Message, T> extends never
      ? [listener: () => void]
      : [listener: (payload: ExtractPayload<Message, T>) => void]
  ): void {
    this.listeners.set(type, args[0] as Listener);
  }

  destroy(): void {
    if (typeof window !== "undefined") {
      window.removeEventListener("message", this.handleMessage);
    }
    this.listeners.clear();
  }

  private isValidOrigin(origin: string): boolean {
    const host = this.parseOrigin(origin);
    return Boolean(
      host === "localhost" ||
      host.endsWith(".intelligems.io") ||
      host.endsWith("rproxyserv.com")
    );
  }

  private parseOrigin(origin: string) {
    try {
      return new URL(origin).hostname;
    } catch {
      return "";
    }
  }
}

type ExtractPayload<
  M extends { type: string },
  T extends M["type"],
> = "payload" extends keyof Extract<M, { type: T }>
  ? Extract<M, { type: T }>["payload"]
  : "action" extends keyof Extract<M, { type: T }>
    ? Extract<M, { type: T }>["action"]
    : never;

/**
 * Configuration for MessageProxy.
 */
interface MessageProxyConfig {
  /** Window to send messages to (e.g. iframe.contentWindow or window.parent) */
  getTargetWindow(): Window | undefined;
  /** Log debug info to console */
  debug?: boolean;
}
